'use client';

import {
  Alert,
  Autocomplete,
  FormControl,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';

import { EmptySelect } from './emptySelect';

interface ParameterGroup {
  name?: string;
  family?: string;
  description?: string;
}

interface ParameterGroupResponse {
  parameterGroups?: ParameterGroup[];
}

const title = 'DB cluster parameter group';

export const DbClusterParameterGroupSelect = ({
  accountId,
  regionName,
  parameterGroupName,
  onChange,
  useList,
  engineLabel,
  testId,
}: {
  accountId?: string;
  regionName?: string;
  parameterGroupName?: string;
  onChange: (name: string) => void;
  useList: (
    accountId?: string,
    regionName?: string
  ) => { data: ParameterGroupResponse | undefined; isLoading: boolean };
  engineLabel: string;
  testId: string;
}) => {
  if (!accountId || !regionName) {
    return <EmptySelect title={title} />;
  }

  return (
    <InnerSelect
      accountId={accountId}
      regionName={regionName}
      parameterGroupName={parameterGroupName}
      onChange={onChange}
      useList={useList}
      engineLabel={engineLabel}
      testId={testId}
    />
  );
};

const InnerSelect = ({
  accountId,
  regionName,
  parameterGroupName,
  onChange,
  useList,
  engineLabel,
  testId,
}: {
  accountId: string;
  regionName: string;
  parameterGroupName?: string;
  onChange: (name: string) => void;
  useList: (
    accountId?: string,
    regionName?: string
  ) => { data: ParameterGroupResponse | undefined; isLoading: boolean };
  engineLabel: string;
  testId: string;
}) => {
  const { data: body, isLoading } = useList(accountId, regionName);

  const groups = body?.parameterGroups ?? [];

  if (!isLoading && !groups.length) {
    return (
      <FormControl size='small' className='flex-1'>
        <FormLabel>{title}</FormLabel>
        <Alert severity='warning' className='mt-[4px]'>
          <Typography variant='body2'>
            {`No ${engineLabel} cluster parameter groups found in this region.`}
          </Typography>
        </Alert>
      </FormControl>
    );
  }

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>{title}</FormLabel>
      <Autocomplete
        data-testid={testId}
        clearOnBlur
        size='small'
        loading={isLoading}
        value={groups.find((pg) => pg.name === parameterGroupName) || null}
        onChange={(_event, newValue) => onChange(newValue?.name || '')}
        getOptionLabel={(option) => `${option.name} (${option.family})`}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder='Select parameter group'
            label=''
            slotProps={{ inputLabel: { shrink: true } }}
          />
        )}
        options={groups}
      />
    </FormControl>
  );
};
